import { convert } from '@js/utils/currencyUtils';
import { DEFAULT_CURRENCY } from '@js/core/currencies.js';
import { shortFormat } from '@js/utils/moneyUtils';
import { CONNECTION_TYPES } from '@js/nal/constants/searchFilter.js';

const _teamSizes = ['<10', '10+', '25+', '50+', '100+', '150+', '200+'];

export function getCurrency() {
    return _(window.nal).get('user.settings.currency', DEFAULT_CURRENCY);
}

export function getTeamSizes() {
    return _teamSizes;
}

export function getRecentLabel(months) {
    if (months === null) {
        return trans('nal-lang.search.filter.any-time');
    }

    if (months === 1) {
        return trans('nal-lang.search.filter.past-month');
    }

    return trans('nal-lang.search.filter.past-value-months', { number: months });
}

export function getConnectionLabelArray(filter) {
    const tags = CONNECTION_TYPES
        .filter(item => filter[item.key])
        .map(item => trans(item.label));

    if (tags.length === 0) {
        return null;
    }

    return tags;
}

export function getSalaryLabel(filter) {
    const currency = getCurrency();
    const amount = filter.range.map(value => convert(value, currency, 'GBP'));

    const lowSalary = amount[0];
    const highSalary = amount[1];

    return `${shortFormat(lowSalary, currency)} - ${shortFormat(highSalary)}`;
}

export function getStatusLabel(filter) {
    function getLabel(number) {
        return trans(`common.statuses.${number}`);
    }
    if (filter.range[0] === filter.range[1]) {
        return getLabel(filter.range[0]);
    }

    return getLabel(filter.range[0]) + ' - ' + getLabel(filter.range[1]);
}

export function getTeamLabel(filter) {
    const teamSizes = getTeamSizes();
    if (filter.range.length === 0) {
        return null;
    }

    const getLabel = (value) => {
        switch (value) {
            case 1:
                return trans('nal-lang.search.team.none');
            case 2:
                value = 0;
                break;
            case 3:
                value = 1;
                break;
            case 4:
                value = 3;
                break;
            case 5:
                value = 6;
                break;
            case 6:
                value = 2;
                break;
            case 7:
                value = 4;
                break;
            case 8:
                value = 5;
                break;
            default:
                break;
        }

        return trans(
            'nal-lang.search.team.people',
            { number: _teamSizes[value] }
        );
    };

    if (filter.range[0] === filter.range[1]) {
        return getLabel(filter.range[0]);
    }

    return getLabel(filter.range[0]) + ' - ' + getLabel(filter.range[1]);
}

export function getPositionTypeLabel(filter) {
    return trans('common.positions.' + filter.value);
}

export function getFilterLabel(category, filter) {
    let label;
    switch (category) {
        case 'recent':
            label = getRecentLabel(filter.months);
            break;
        case 'connection':
            label = getConnectionLabelArray(filter);
            break;
        case 'salary':
            label = getSalaryLabel(filter);
            break;
        case 'status':
            label = getStatusLabel(filter);
            break;
        case 'team':
            label = getTeamLabel(filter);
            break;
        case 'position_type':
            label = getPositionTypeLabel(filter);
            break;
        default:
            label = null;
    }
    return label;
}

export function cleanupFilterBoxes(filtersToClean, availableBoxes) {
    if (!availableBoxes || !filtersToClean) {
        return;
    }

    for (const category in filtersToClean) {
        const filter = filtersToClean[category];
        const categoryBoxes = availableBoxes[category];
        const hasFilterBoxes = Array.isArray(filter?.boxes);
        const hasCategoryBoxes = Array.isArray(categoryBoxes);

        if (!hasFilterBoxes || !hasCategoryBoxes) {
            continue;
        }

        const allowedValues = new Set(categoryBoxes.map(box => box.value));
        filter.boxes = filter.boxes.filter(value => allowedValues.has(value));
    }
}

export function cleanupCategoryFilterBoxes(filter, categoryBoxes) {
    const hasValidFilterBoxes = filter?.boxes && Array.isArray(filter.boxes) && filter.boxes.length > 0;
    const hasValidCategoryBoxes = categoryBoxes && Array.isArray(categoryBoxes) && categoryBoxes.length > 0;

    if (!hasValidFilterBoxes || !hasValidCategoryBoxes) {
        return;
    }

    const availableIds = new Set(categoryBoxes.map(box => box.value));
    const filteredBoxes = filter.boxes.filter(id => availableIds.has(id));

    if (!_.isEqual(filter.boxes, filteredBoxes)) {
        filter.boxes = filteredBoxes;
    }
}

export function getFilterBoxTagLabel(filter, categoryBoxes) {
    if (!filter || !Array.isArray(filter.boxes) || !Array.isArray(categoryBoxes)) {
        return null;
    }

    const selectedBoxTexts = filter.boxes
        .map(boxId => categoryBoxes.find(box => box.value === boxId)?.text)
        .filter(Boolean);

    return selectedBoxTexts.length > 0 ? selectedBoxTexts : null;
}

export function compactFilters(filters) {
    if (!filters || typeof filters !== 'object') {
        return {};
    }
    const reduced = {};
    for (const key in filters) {
        const filter = filters[key];
        const boxes = filter?.boxes;
        if (!boxes || boxes.length > 0) {
            reduced[key] = filter;
        }
    }
    return reduced;
}
